import type { InfiniteData, QueryClient } from '@tanstack/react-query';

import type {
  ChatAccessDto,
  ChatDto,
  ChatLastMessageDto,
  MatchUpdatedDto,
  MessageDto,
  MessageListDto,
  MessagesReadDto,
  PresenceDto,
} from '@/src/entities/chats/api/dto';
import { chatQueries } from '@/src/entities/chats/api/queries';
import type { UserRole } from '@/src/shared/model/types';

import { useOpenChatStore } from './open-chat';

function writeChats(
  queryClient: QueryClient,
  updater: (chats: ChatDto[]) => ChatDto[],
) {
  queryClient.setQueriesData({ queryKey: chatQueries.all }, (current) => {
    if (!Array.isArray(current)) {
      return current;
    }

    return updater(current as ChatDto[]);
  });
}

function activityTime(chat: ChatDto) {
  return Date.parse(chat.lastMessage?.createdAt ?? chat.createdAt);
}

export function upsertMessage(
  queryClient: QueryClient,
  message: MessageDto,
  viewerId?: string | null,
) {
  queryClient.setQueryData<InfiniteData<MessageListDto>>(
    chatQueries.messages(message.chatId),
    (current) => {
      if (!current?.pages.length) {
        return current;
      }

      const exists = current.pages.some((page) =>
        page.items.some((item) => item.id === message.id),
      );

      if (exists) {
        return current;
      }

      const [latest, ...older] = current.pages;

      return {
        ...current,
        pages: [{ ...latest, items: [...latest.items, message] }, ...older],
      };
    },
  );

  writeChats(queryClient, (chats) => {
    if (!chats) {
      return chats;
    }

    const known = chats.some((chat) => chat.id === message.chatId);

    if (!known) {
      void queryClient.invalidateQueries({ queryKey: chatQueries.all });
      return chats;
    }

    const lastMessage: ChatLastMessageDto = {
      id: message.id,
      text: message.text,
      senderId: message.senderId,
      readAt: message.readAt,
      createdAt: message.createdAt,
    };
    const openChatId = useOpenChatStore.getState().chatId;

    return chats
      .map((chat) => {
        if (chat.id !== message.chatId) {
          return chat;
        }

        const alreadyLast = chat.lastMessage?.id === message.id;
        const fromOther = Boolean(viewerId) && message.senderId !== viewerId;
        const unreadBump =
          !alreadyLast && fromOther && openChatId !== chat.id ? 1 : 0;

        return {
          ...chat,
          lastMessage,
          requestMessage:
            message.kind === 'REQUEST' ? message : chat.requestMessage,
          unreadCount: chat.unreadCount + unreadBump,
        };
      })
      .sort((left, right) => activityTime(right) - activityTime(left));
  });
}

export function applyMessagesRead(
  queryClient: QueryClient,
  payload: MessagesReadDto,
  viewerId?: string | null,
) {
  const readIds = new Set(payload.messageIds);

  queryClient.setQueryData<InfiniteData<MessageListDto>>(
    chatQueries.messages(payload.chatId),
    (current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        pages: current.pages.map((page) => ({
          ...page,
          items: page.items.map((item) =>
            readIds.has(item.id) ? { ...item, readAt: payload.readAt } : item,
          ),
        })),
      };
    },
  );

  writeChats(queryClient, (chats) => {
    if (!chats) {
      return chats;
    }

    return chats.map((chat) => {
      if (chat.id !== payload.chatId) {
        return chat;
      }

      const lastMessage =
        chat.lastMessage && readIds.has(chat.lastMessage.id)
          ? { ...chat.lastMessage, readAt: payload.readAt }
          : chat.lastMessage;
      const requestMessage =
        chat.requestMessage && readIds.has(chat.requestMessage.id)
          ? { ...chat.requestMessage, readAt: payload.readAt }
          : chat.requestMessage;

      return {
        ...chat,
        lastMessage,
        requestMessage,
        unreadCount: payload.readerId === viewerId ? 0 : chat.unreadCount,
      };
    });
  });
}

export function applyPresence(queryClient: QueryClient, payload: PresenceDto) {
  writeChats(queryClient, (chats) => {
    if (!chats) {
      return chats;
    }

    return chats.map((chat) => {
      if (chat.counterpart.userId !== payload.userId) {
        return chat;
      }

      return {
        ...chat,
        counterpart: {
          ...chat.counterpart,
          isOnline: payload.isOnline,
          lastSeenAt: payload.lastSeenAt,
        },
      };
    });
  });
}

export function applyMatchUpdate(
  queryClient: QueryClient,
  payload: MatchUpdatedDto,
  role?: UserRole,
) {
  writeChats(queryClient, (chats) => {
    if (!chats) {
      return chats;
    }

    return chats.map((chat) => {
      if (chat.id !== payload.chatId) {
        return chat;
      }

      const myDecision =
        role === 'TEACHER' ? payload.teacherDecision : payload.studentDecision;

      return {
        ...chat,
        matchStatus: payload.matchStatus,
        isReadOnly: payload.isReadOnly,
        myDecision,
      };
    });
  });
}

export function replaceMessage(queryClient: QueryClient, message: MessageDto) {
  queryClient.setQueryData<InfiniteData<MessageListDto>>(
    chatQueries.messages(message.chatId),
    (current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        pages: current.pages.map((page) => ({
          ...page,
          items: page.items.map((item) =>
            item.id === message.id ? message : item,
          ),
        })),
      };
    },
  );

  writeChats(queryClient, (chats) =>
    chats.map((chat) => {
      if (chat.lastMessage?.id !== message.id) {
        return chat;
      }

      return {
        ...chat,
        lastMessage: { ...chat.lastMessage, text: message.text },
      };
    }),
  );
}

export function applyChatAccess(
  queryClient: QueryClient,
  payload: ChatAccessDto,
) {
  writeChats(queryClient, (chats) =>
    chats.map((chat) => {
      if (chat.id !== payload.chatId) {
        return chat;
      }

      return {
        ...chat,
        isReadOnly: payload.isReadOnly,
        isBlocked: payload.isBlocked,
        blockedByMe:
          payload.blockedByMe === null ? chat.blockedByMe : payload.blockedByMe,
      };
    }),
  );
}
